import { useCallback } from 'react';

interface UseSpreadsheetKeyboardOptions {
  rows: any[];
  setRows: React.Dispatch<React.SetStateAction<any[]>>;
  inputRefs: React.MutableRefObject<(HTMLInputElement | HTMLSelectElement | null)[][]>;
  createEmptyRow: () => any;
  hasRowData: (row: any) => boolean;
  maxColumns?: number;
}

/**
 * Shared keyboard navigation hook for spreadsheet components
 * Handles Enter, ArrowUp, ArrowDown, ArrowLeft, ArrowRight keys
 *
 * Enter = Navigate down / add new row (does NOT save)
 * ArrowUp/Down = Navigate between rows
 * ArrowLeft/Right = Navigate between columns
 */
export const useSpreadsheetKeyboard = ({
  rows,
  setRows,
  inputRefs,
  createEmptyRow,
  hasRowData,
  maxColumns = 20
}: UseSpreadsheetKeyboardOptions) => {

  // Focus on a specific cell
  const goTo = useCallback((r: number, c: number): boolean => {
    const input = inputRefs.current[r]?.[c];
    if (input) {
      input.focus();
      return true;
    }
    return false;
  }, [inputRefs]);

  // Find next valid column to the right
  const findNextColumn = useCallback((r: number, currentCol: number): number | null => {
    const rowInputs = inputRefs.current[r];
    if (!rowInputs) return null;

    for (let c = currentCol + 1; c <= maxColumns + 5; c++) {
      if (rowInputs[c]) return c;
    }
    return null;
  }, [inputRefs, maxColumns]);

  // Find previous valid column to the left
  const findPrevColumn = useCallback((r: number, currentCol: number): number | null => {
    const rowInputs = inputRefs.current[r];
    if (!rowInputs) return null;

    for (let c = currentCol - 1; c >= 0; c--) {
      if (rowInputs[c]) return c;
    }
    return null;
  }, [inputRefs]);

  // Navigate to row, trying same column first, then finding nearest valid column
  const navigateToRow = useCallback((targetRow: number, currentCol: number) => {
    if (!goTo(targetRow, currentCol)) {
      const prevCol = findPrevColumn(targetRow, currentCol);
      const nextCol = findNextColumn(targetRow, currentCol);
      if (prevCol !== null) goTo(targetRow, prevCol);
      else if (nextCol !== null) goTo(targetRow, nextCol);
    }
  }, [goTo, findPrevColumn, findNextColumn]);

  // Main keyboard handler
  const handleKeyDown = useCallback((
    e: React.KeyboardEvent<HTMLElement>,
    rowIdx: number,
    colIdx: number
  ) => {
    if (e.isDefaultPrevented()) return;

    switch (e.key) {
      case "Enter": {
        // Enter key navigates down / adds new row (like Excel)
        // Does NOT save - use the Save button for that
        const target = e.target as HTMLElement;
        if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'SELECT')) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const nextRowIndex = rowIdx + 1;
        const currentRow = rows[rowIdx];
        const currentRowHasData = hasRowData(currentRow);

        if (!rows[nextRowIndex]) {
          // Only create new row if current row has some data
          if (currentRowHasData) {
            setRows(prev => [...prev, createEmptyRow()]);
            setTimeout(() => {
              goTo(nextRowIndex, colIdx);
            }, 0);
          }
        } else {
          // Navigate to existing next row
          navigateToRow(nextRowIndex, colIdx);
        }
        break;
      }

      case "ArrowUp":
        // Unconditional: on the first row this must still swallow the event, or a
        // type="number" cell falls through to the browser's spinner and increments.
        e.preventDefault();
        if (rowIdx > 0) {
          navigateToRow(rowIdx - 1, colIdx);
        }
        break;

      case "ArrowDown": {
        e.preventDefault();
        const nextRowIndex = rowIdx + 1;
        const currentRow = rows[rowIdx];
        const currentRowHasData = hasRowData(currentRow);

        if (!rows[nextRowIndex]) {
          // Only create new row if current row has some data
          if (currentRowHasData) {
            setRows(prev => [...prev, createEmptyRow()]);
            setTimeout(() => {
              goTo(nextRowIndex, colIdx);
            }, 0);
          }
        } else {
          navigateToRow(nextRowIndex, colIdx);
        }
        break;
      }

      case "ArrowLeft": {
        e.preventDefault();
        const prevCol = findPrevColumn(rowIdx, colIdx);
        if (prevCol !== null) {
          goTo(rowIdx, prevCol);
        }
        break;
      }

      case "ArrowRight": {
        e.preventDefault();
        const nextCol = findNextColumn(rowIdx, colIdx);
        if (nextCol !== null) {
          goTo(rowIdx, nextCol);
        }
        break;
      }
    }
  }, [rows, setRows, createEmptyRow, hasRowData, goTo, navigateToRow, findPrevColumn, findNextColumn]);

  return {
    handleKeyDown,
    goTo,
    findNextColumn,
    findPrevColumn,
    navigateToRow
  };
};

export default useSpreadsheetKeyboard;
